/**
 * @file API service — publicationService.
 * Publication Service
 * All API calls related to publication details
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";
import { FormDataResponse } from "../interfaces/formInterfaces";

/** Type definition — Publication Payload. */
export interface PublicationPayload {
  [key: string]: string | File | undefined;
}

/**
 * Get form data for publication details stage
 */
export const getPublicationFormData = async (
  stageCode: string
): Promise<{
  success: boolean;
  data: FormDataResponse | null;
  error?: string;
}> => {
  try {
    const response = await axiosClient.get(API_URLS.FORMS_STAGE(stageCode));
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to fetch form data",
      data: null,
    };
  }
};

/**
 * Get submission by ID
 */
export const getSubmission = async (
  submissionId: string
): Promise<{
  success: boolean;
  data: any | null;
  error?: string;
}> => {
  try {
    const response = await axiosClient.get(API_URLS.SUBMISSIONS.BY_ID(submissionId));
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to fetch submission",
      data: null,
    };
  }
};

/**
 * Save publication details
 */
export const savePublicationDetails = async (
  submissionId: string,
  payload: PublicationPayload
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  try {
    const hasFile = Object.values(payload || {}).some((v) => v instanceof File);

    const url = API_URLS.SUBMISSIONS.PUBLICATION(submissionId);

    const response = hasFile
      ? await axiosClient.put(url, toFormData(payload), {
          headers: { "Content-Type": "multipart/form-data" },
        })
      : await axiosClient.put(url, payload);
    return {
      success: true,
      message: response.data?.message || "Publication details saved successfully",
    };
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to save publication details";
    return {
      success: false,
      error: errorMessage,
    };
  }
};

function toFormData(payload: PublicationPayload) {
  const fd = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (value instanceof File) {
      fd.append(key, value);
    } else {
      fd.append(key, String(value));
    }
  });
  return fd;
}

